import { POLICY_STATUS, RATE_PLAN_MEAL_PLANS, RATE_PLAN_TYPE } from '@ibe/constants';
import { z } from 'zod';

/**
 * The Rate Plans module's contracts.
 */

const MAX_NAME = 255;
const MAX_DESCRIPTION = 4000;
const MAX_CODE = 32;
const PMS_RATE_ID_MAX = 255;
const CODE_MIN_LENGTH = 2;
const CURRENCY_LENGTH = 3;
const MAX_DAYS = 999;
const CODE_PATTERN = /^[A-Z0-9-]{2,32}$/u;
const CURRENCY_PATTERN = /^[A-Z]{3}$/u;

/** Numeric string that may be empty, because a length or advance limit is optional. */
function numericString(label: string, max: number): z.ZodType<string, string> {
  return z
    .string()
    .trim()
    .regex(/^\d*$/, `${label} must be a whole number`)
    .refine(
      (value) => {
        if (value === '') return true;
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 && parsed <= max;
      },
      `${label} must be between 0 and ${String(max)}`,
    );
}

export const RATE_PLAN_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: POLICY_STATUS.ACTIVE, label: 'Active' },
  { value: POLICY_STATUS.INACTIVE, label: 'Inactive' },
  { value: POLICY_STATUS.ARCHIVED, label: 'Archived' },
];

const statusValues = RATE_PLAN_STATUS_OPTIONS.map((option) => option.value) as [
  string,
  ...string[],
];

export const RATE_PLAN_MEAL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: RATE_PLAN_MEAL_PLANS.NONE, label: 'Room only' },
  { value: RATE_PLAN_MEAL_PLANS.BREAKFAST, label: 'Breakfast' },
  { value: RATE_PLAN_MEAL_PLANS.HALF_BOARD, label: 'Half board' },
  { value: RATE_PLAN_MEAL_PLANS.FULL_BOARD, label: 'Full board' },
  { value: RATE_PLAN_MEAL_PLANS.ALL_INCLUSIVE, label: 'All inclusive' },
];

const mealValues = RATE_PLAN_MEAL_OPTIONS.map((option) => option.value) as [string, ...string[]];

export const RATE_PLAN_TYPE_OPTIONS: readonly { value: string; label: string }[] = Object.values(
  RATE_PLAN_TYPE,
).map((value) => ({ value, label: value }));

const typeValues = RATE_PLAN_TYPE_OPTIONS.map((option) => option.value) as [string, ...string[]];

/** A calendar date as the <input type="date"> holds it, or empty. */
function dateString(label: string): z.ZodType<string, string> {
  return z.string().refine((value) => value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value), {
    message: `${label} must be a date in YYYY-MM-DD format`,
  });
}

export const ratePlanRow = z.object({
  id: z.string(),
  propertyId: z.string().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  currency: z.string().optional(),
  status: z.string().optional(),
  refundable: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type RatePlanRow = z.infer<typeof ratePlanRow>;

export const ratePlanDetail = z.object({
  id: z.string(),
  propertyId: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.string(),
  description: z.string().nullable(),
  currency: z.string(),
  mealPlan: z.string(),
  refundable: z.boolean(),
  status: z.string(),
  roomTypeIds: z.array(z.string()),
  minLengthOfStay: z.number().nullable(),
  maxLengthOfStay: z.number().nullable(),
  advanceBookingDays: z.number().nullable(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
  cancellationPolicyId: z.string().nullable(),
  paymentPolicyId: z.string().nullable(),
  pmsRateId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
});

export type RatePlanDetail = z.infer<typeof ratePlanDetail>;

export const ratePlanFormInput = z.object({
  propertyId: z.string().min(1, 'Choose the property this rate plan belongs to'),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .min(CODE_MIN_LENGTH, 'Code must be at least 2 characters')
    .max(MAX_CODE, `Code must be ${String(MAX_CODE)} characters or fewer`)
    .regex(CODE_PATTERN, 'Code can only contain uppercase letters, digits and hyphens'),
  name: z
    .string()
    .trim()
    .min(1, 'A rate plan needs a name')
    .max(MAX_NAME, `Keep the name under ${String(MAX_NAME)} characters`),
  type: z.enum(typeValues),
  description: z
    .string()
    .trim()
    .max(MAX_DESCRIPTION, `Keep the description under ${String(MAX_DESCRIPTION)} characters`),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .length(CURRENCY_LENGTH, 'Currency must be a 3-letter ISO 4217 code')
    .regex(CURRENCY_PATTERN, 'Currency must be an ISO 4217 code such as USD or AUD'),
  mealPlan: z.enum(mealValues),
  refundable: z.boolean(),
  status: z.enum(statusValues),
  roomTypeIds: z.array(z.string()),
  minLengthOfStay: numericString('Minimum length of stay', MAX_DAYS),
  maxLengthOfStay: numericString('Maximum length of stay', MAX_DAYS),
  advanceBookingDays: numericString('Advance booking days', MAX_DAYS),
  activeFrom: dateString('Active from'),
  activeTo: dateString('Active to'),
  cancellationPolicyId: z.string(),
  paymentPolicyId: z.string(),
  pmsRateId: z
    .string()
    .max(PMS_RATE_ID_MAX, `Keep the PMS rate id under ${String(PMS_RATE_ID_MAX)} characters`),
});

export type RatePlanFormInput = z.infer<typeof ratePlanFormInput>;

export const createdRatePlan = z.object({ id: z.string() });

export type CreatedRatePlan = z.infer<typeof createdRatePlan>;
